import posthog from "posthog-js";

/* ──────────────────────────────────────────────────────────────────────────
 * Funnel analytics — typed event helpers.
 *
 * One place that knows the names and shapes of every custom funnel event, so
 * call sites stay declarative and the read layer (scripts/analytics) and the
 * event taxonomy doc (docs/analytics/README.md) have a single source of truth.
 *
 * The activation funnel we instrument:
 *   land → simulate_cta_clicked → (pageview /simulate) → simulator_engaged
 *        → tracker_cta_clicked → (pageview /tracker) → execute_waitlist_signup
 *
 * `execute_waitlist_signup` is fired server-side (see tracker/actions.ts) and
 * is intentionally NOT re-declared here.
 *
 * Note on init: on localhost PostHog is never initialized (see analytics-env),
 * so these calls are safe no-ops in dev. `posthog.capture` is always callable.
 * ────────────────────────────────────────────────────────────────────────── */

/** Funnel CTA events that carry a `source`. */
export type FunnelEvent = "simulate_cta_clicked" | "tracker_cta_clicked";

/**
 * Where a funnel CTA was clicked. Only intentional conversion CTAs are
 * instrumented — passive nav (footer / command palette, and the plain text
 * links in the header) is left to pageviews + autocapture to avoid noise.
 * `site_header` is the deliberate exception: the persistent primary CTA button
 * in the header is a conversion CTA, not passive nav, so it carries a source.
 */
export type FunnelSource =
  | "home_hero"
  | "home_journey"
  | "home_closing"
  | "how_it_works"
  | "simulator_hook"
  | "site_header";

/** Which simulator control the user first touched. */
export type SimulatorControl =
  | "ticker"
  | "borrowed"
  | "lending_type"
  | "window"
  | "dividend_handling"
  | "income"
  | "jurisdiction"
  | "breakdown";

/** Fire a funnel CTA click-through event. */
export function captureCtaClick(event: FunnelEvent, source: FunnelSource): void {
  posthog.capture(event, { source });
}

/**
 * Fire the simulator engagement event. The caller fires this once per visit,
 * on the first real input change — the signal that distinguishes "used the
 * simulator" from "viewed the page" (which autocapture cannot derive).
 */
export function captureSimulatorEngaged(props: {
  first_interaction: SimulatorControl;
  ticker: string;
  borrowed_bucket: string;
}): void {
  posthog.capture("simulator_engaged", props);
}

/**
 * Bucket a hypothetical borrowed amount. We deliberately never log the raw
 * figure — it's simulator input, not a real account balance, but bucketing
 * keeps the analytics clean and consistent with the `.nk-numeric` session-
 * replay mask. Buckets are inclusive of the lower bound.
 */
export function borrowedBucket(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return "none";
  if (amount < 50_000) return "lt_50k";
  if (amount < 100_000) return "50k_100k";
  if (amount < 250_000) return "100k_250k";
  if (amount < 500_000) return "250k_500k";
  if (amount < 1_000_000) return "500k_1m";
  return "1m_plus";
}
